import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';

import {
  Colors,
  FontSizes,
  Theme,
} from 'common';

import {
  Text,
} from 'common/components';

const Container = styled.div`
  position: relative;
  margin: ${props => props.margin || '0 0 10px 0'};
  width: ${props => props.width || '250px'};
`;

const SelectContainer = styled.div`
  border: ${props => props.hasError ? Theme.TextInput.BorderError : Theme.TextInput.Border};
  background-color: ${props => props.backgroundColor || Theme.TextInput.Background};
  display: flex;
  padding: 5px 15px;
  position: relative;
  border-radius: ${Theme.TextInput.BorderRadius};
  align-items: center;
  width: 100%;
  height: ${props => props.height || '40px'};
  cursor: pointer;
  justify-content: space-between;
`;

const SelectValue = styled.div`
  color: ${props => props.hasValue ? Theme.Text.Color : Colors.Grey};
  font-size: ${FontSizes.Small};
  font-style: ${props => props.hasValue ? 'normal' : 'italic'};
  flex: 1;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClearButton = styled.div`
  width: 16px;
  height: 16px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 100%;
    height: 100%;
    fill: ${Theme.Text.Color};
  }
`;

const DropdownIcon = styled.div`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  svg {
    width: 100%;
    height: 100%;
    fill: ${Theme.Text.Color};
  }
`;

const OptionsContainer = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  background-color: ${Theme.TextInput.Background};
  border: ${Theme.TextInput.Border};
  border-radius: ${Theme.TextInput.BorderRadius};
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SearchContainer = styled.div`
  padding: 10px;
  border-bottom: 1px solid ${Colors.Grey}30;
  position: sticky;
  top: 0;
  background-color: ${Theme.TextInput.Background};
`;

const SearchInput = styled.input`
  width: 100%;
  border: ${Theme.TextInput.Border};
  background-color: ${Theme.TextInput.Background};
  color: ${Theme.Text.Color};
  font-size: ${FontSizes.Small};
  padding: 5px 10px;
  border-radius: ${Theme.TextInput.BorderRadius};
  
  &:focus {
    outline: none;
    border-color: ${Colors.Grey};
  }
  
  &::placeholder {
    color: ${Colors.Grey};
    font-style: italic;
  }
`;

const Option = styled.div`
  padding: 12px 15px;
  cursor: pointer;
  color: ${Theme.Text.Color};
  font-size: ${FontSizes.Small};
  min-height: 44px;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: ${Colors.Grey}20;
  }
  
  &:active {
    background-color: ${Colors.Grey}30;
  }
  
  &:last-child {
    border-bottom-left-radius: ${Theme.TextInput.BorderRadius};
    border-bottom-right-radius: ${Theme.TextInput.BorderRadius};
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    min-height: 48px;
    font-size: ${FontSizes.Normal};
  }
`;

const NoOptionsMessage = styled.div`
  padding: 15px;
  text-align: center;
  color: ${Colors.Grey};
  font-size: ${FontSizes.Small};
  font-style: italic;
`;

const ErrorMessage = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  top: 5px;
  right: 3px;
  position: relative;
  color: ${Colors.PrimaryOne};
  font-size: ${FontSizes.Smaller};
`;

export const Select = props => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(props.value || '');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('below');
  const [maxHeight, setMaxHeight] = useState(220);
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);

  const options = props.options || [];
  const hasSearch = options.length > 15;

  // Filter options based on search term
  const filteredOptions = searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        option.value.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Find the selected option to display its label
  const selectedOption = options.find(option => option.value === selectedValue);
  const displayValue = selectedOption ? selectedOption.label : '';

  // Calculate optimal dropdown position and height
  const calculateDropdownPosition = useCallback(() => {
    if (!selectRef.current) return;

    const selectRect = selectRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - selectRect.bottom;
    const spaceAbove = selectRect.top;
    const isMobile = window.innerWidth <= 768;
    
    // Estimate dropdown height (account for mobile larger touch targets)
    const searchHeight = hasSearch ? 60 : 0;
    const optionHeight = isMobile ? 48 : 44;
    const estimatedDropdownHeight = Math.min(
      isMobile ? 300 : 220, 
      (filteredOptions.length * optionHeight) + searchHeight + 20
    );
    
    // On mobile, prefer using more screen space
    const mobileMargin = isMobile ? 10 : 20;
    
    // Determine position based on available space
    if (spaceBelow >= estimatedDropdownHeight || spaceBelow >= spaceAbove) {
      setDropdownPosition('below');
      // Use available space below, with margin from bottom
      setMaxHeight(Math.min(
        isMobile ? 300 : 220, 
        Math.max(120, spaceBelow - mobileMargin)
      ));
    } else {
      setDropdownPosition('above');
      // Use available space above, with margin from top
      setMaxHeight(Math.min(
        isMobile ? 300 : 220, 
        Math.max(120, spaceAbove - mobileMargin)
      ));
    }
  }, [hasSearch, filteredOptions.length]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleResize = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        calculateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, calculateDropdownPosition]);

  // Update selected value when props.value changes
  useEffect(() => {
    setSelectedValue(props.value || '');
  }, [props.value]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && hasSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [isOpen, hasSearch]);

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();
    }
  }, [isOpen, filteredOptions.length, hasSearch, calculateDropdownPosition]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleOptionSelect = (option) => {
    setSelectedValue(option.value);
    setIsOpen(false);
    setSearchTerm('');
    
    if (props.onChange) {
      props.onChange(option.value);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedValue('');
    
    if (props.onChange) {
      props.onChange('');
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e) => {
    // Prevent the dropdown from closing when typing in search
    e.stopPropagation();
  };

  return (
    <Container
      margin={props.margin}
      width={props.width}
      ref={selectRef}>

      {props.label && (
        <Text
          fontSize={FontSizes.Small}
          padding={'0 0 10px 0'}>
          {props.label}
        </Text>
      )}

      <SelectContainer
        height={props.height}
        hasError={props.hasError}
        backgroundColor={props.backgroundColor}
        onClick={handleToggle}>

        <SelectValue hasValue={!!displayValue}>
          {displayValue || props.placeholder || 'Select...'}
        </SelectValue>

        <IconContainer>
          {displayValue && (
            <ClearButton onClick={handleClear}>
              <svg viewBox="0 0 1024 1024">
                <path d="M685.4 354.8c0-4.4-3.6-8-8-8l-66 0.3L512 465.6l-99.3-118.4-66.1-0.3c-4.4 0-8 3.5-8 8 0 1.9 0.7 3.7 1.9 5.2l130.1 155L340.5 670c-1.2 1.5-1.9 3.3-1.9 5.2 0 4.4 3.6 8 8 8l66.1-0.3L512 564.4l99.3 118.4 66 0.3c4.4 0 8-3.5 8-8 0-1.9-0.7-3.7-1.9-5.2L553.5 515l130.1-155c1.2-1.4 1.8-3.3 1.8-5.2z" />
                <path d="M512 65C264.6 65 64 265.6 64 513s200.6 448 448 448 448-200.6 448-448S759.4 65 512 65z m0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z" />
              </svg>
            </ClearButton>
          )}

          <DropdownIcon isOpen={isOpen}>
            <svg viewBox="0 0 1024 1024">
              <path d="M884 256h-75c-5.1 0-9.9 2.5-12.9 6.6L512 654.2 227.9 262.6c-3-4.1-7.8-6.6-12.9-6.6h-75c-6.5 0-10.3 7.4-6.5 12.7l352.6 486.1c12.8 17.6 39 17.6 51.7 0l352.6-486.1c3.9-5.3 0.1-12.7-6.4-12.7z" />
            </svg>
          </DropdownIcon>
        </IconContainer>
      </SelectContainer>

      {isOpen && (
        <OptionsContainer
          style={{
            [dropdownPosition === 'above' ? 'bottom' : 'top']: '100%',
            maxHeight: `${maxHeight}px`,
            borderTop: dropdownPosition === 'above' ? Theme.TextInput.Border : 'none',
            borderBottom: dropdownPosition === 'above' ? 'none' : undefined,
          }}>
          {hasSearch && (
            <SearchContainer>
              <SearchInput
                ref={searchInputRef}
                type="text"
                placeholder="Search options..."
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
              />
            </SearchContainer>
          )}
          
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <Option
                key={index}
                onClick={() => handleOptionSelect(option)}>
                {option.label}
              </Option>
            ))
          ) : (
            <NoOptionsMessage>
              {searchTerm ? 'No options match your search' : 'No options available'}
            </NoOptionsMessage>
          )}
        </OptionsContainer>
      )}

      {props.hasError && props.errorMessage && (
        <ErrorMessage>
          {props.errorMessage}
        </ErrorMessage>
      )}

    </Container>
  );
};